import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { ZoomIn, Loader2, Check, Frame } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const CARD_ASPECT = 5 / 6; // MarketCard görsel oranı
const TARGET_W = 1000;
const TARGET_H = 1200; // 5:6 sabit çıktı — tüm ürünler aynı tuval boyutunda
// Kırpma alanı (deterministik — kılavuz çerçeve buna göre hizalanır)
const CROP_H = 312;
const CROP_W = Math.round(CROP_H * CARD_ASPECT); // 260
// Hizalama kutusu (ürün alanı) — kırpma alanının % oranı
const GUIDE_W = Math.round(CROP_W * 0.64); // ~166px
const GUIDE_H = Math.round(CROP_H * 0.80); // ~250px

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

// Seçilen kırpma alanını sabit 5:6 tuvale beyaz arka planla çizer.
// Zoom < 1 olduğunda ürün küçülür ve etrafı beyazla dolar (cetvelle hizalama).
async function getCroppedBlob(imageSrc, area) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = TARGET_W;
  canvas.height = TARGET_H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, TARGET_W, TARGET_H);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image, area.x, area.y, area.width, area.height,
    0, 0, TARGET_W, TARGET_H
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/webp", 0.92));
}

export default function ImageCropDialog({ open, file, srcUrl, title, onCancel, onConfirm }) {
  const [imageSrc] = useState(() => (file ? URL.createObjectURL(file) : (srcUrl || null)));
  const isObjectUrl = !!file;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showGuide, setShowGuide] = useState(true);

  const onCropComplete = useCallback((_, areaPx) => setAreaPixels(areaPx), []);

  const confirm = async () => {
    if (!areaPixels || !imageSrc) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels);
      const cropped = new File([blob], "market.webp", { type: "image/webp" });
      await onConfirm(cropped);
    } finally {
      setBusy(false);
      if (isObjectUrl && imageSrc) URL.revokeObjectURL(imageSrc);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent data-testid="market-crop-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Görseli Hizala & Kırp</DialogTitle>
          <DialogDescription className="text-xs">
            {title || "Ürün kartındaki görünüm için kırpın."} Ürünü kesikli <b>hizalama çerçevesine</b> sığdırın — dikey ürünlerde yüksekliği, yatay ürünlerde genişliği çerçeveye oturtun. Böylece tüm ürünler aynı boyutta ve ortada durur.
          </DialogDescription>
        </DialogHeader>

        <div className="relative flex h-80 w-full items-center justify-center overflow-hidden rounded-md bg-white">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={0.3}
              maxZoom={3}
              aspect={CARD_ASPECT}
              cropSize={{ width: CROP_W, height: CROP_H }}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="contain"
              restrictPosition={false}
              style={{ mediaStyle: { background: "#ffffff" } }}
            />
          )}
          {/* Hizalama çerçevesi (ürün alanı) — yalnızca görsel kılavuz, çıktıyı etkilemez */}
          {showGuide && (
            <div
              data-testid="market-crop-guide"
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ width: GUIDE_W, height: GUIDE_H }}
            >
              <div className="h-full w-full rounded-[2px] border-2 border-dashed border-[#C57B43] shadow-[0_0_0_9999px_rgba(0,0,0,0.04)]" />
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-[#C57B43] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white">
                Ürün Alanı
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-1">
          <ZoomIn className="h-4 w-4 shrink-0 text-gray-400" />
          <Slider
            data-testid="market-crop-zoom"
            value={[zoom]}
            min={0.3}
            max={3}
            step={0.01}
            onValueChange={(v) => setZoom(v[0])}
          />
          <span className="w-12 shrink-0 text-right font-mono text-[11px] text-gray-500">
            {Math.round(zoom * 100)}%
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          <label className="flex items-center gap-2 text-[11px] text-gray-600">
            <Switch checked={showGuide} onCheckedChange={setShowGuide} data-testid="market-crop-guide-toggle" />
            <Frame className="h-3.5 w-3.5 text-[#C57B43]" /> Hizalama çerçevesini göster
          </label>
          <span className="text-[11px] text-gray-400">Sola çekerek küçültün, sürükleyerek ortalayın.</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="market-crop-cancel">
            Vazgeç
          </Button>
          <Button
            onClick={confirm}
            disabled={busy || !areaPixels}
            data-testid="market-crop-confirm"
            className="bg-[#C57B43] hover:bg-[#b06d39]"
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}
            Kırp ve Yükle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
